import React, { FC } from "react";

import {
  KpkBasket,
  SentryBreadcrumbCategory,
  basketActiveAtom,
  basketSelector,
  useBasketList,
} from "@keepeek/commons";
import {
  BasketAddTo,
  BasketCreate,
  BasketLayout,
  GenericErrorBoundary,
} from "@keepeek/refront-components";
import { Alert, AlertTitle } from "@mui/material";
import * as Sentry from "@sentry/nextjs";
import Axios from "axios";
import { useRouter } from "next/router";
import { FallbackProps } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue } from "recoil";

import { KIcon } from "../../../components/common/KIcon";
import { currentBasketIdState } from "../../../providers/basket/atoms";
import { configSectionComponentBasketSelector } from "../../../providers/config/selectors";
import { PagesPath } from "../../App/utils";

const EMPTY_BASKET: number = 0;
const BasketError: FC<React.PropsWithChildren<FallbackProps>> = function ({
  error,
  resetErrorBoundary,
  ...rest
}) {
  const router = useRouter();
  const [currentBasketId, setCurrentBasketId] = useRecoilState(currentBasketIdState);
  const currentBasket = useRecoilValue(basketSelector({ id: currentBasketId }));
  const { activateBasketPartitioning } = useRecoilValue(configSectionComponentBasketSelector) ?? {
    activateBasketPartitioning: false,
  };
  const { basketList } = useBasketList({
    basketPartitioning: activateBasketPartitioning,
  });

  const activeBasket = useRecoilValue(basketActiveAtom);
  const selectCurrentBasketId = (basketId: KpkBasket["id"]) => {
    setCurrentBasketId(basketId);

    Sentry.addBreadcrumb({
      category: SentryBreadcrumbCategory.USER_INTERACTION,
      message: `selectCurrentBasketId: ${basketId}`,
    });
  };
  const { t } = useTranslation();
  if (
    Axios.isAxiosError(error) &&
    error.response?.status === 400 &&
    error.response?.data.message === "invalid-fields-value"
  ) {
    // in DEV mode, we can't handle nextjs popup error, so in DEV mode you will have a nextjs 400 error but it's "normal"
    // https://github.com/vercel/next.js/discussions/12818
    return (
      <>
        <BasketLayout
          basketList={basketList}
          elementCount={EMPTY_BASKET}
          currentPage={0}
          active={activeBasket}
          handleBasketSelect={(basketId: KpkBasket["id"]) => {
            resetErrorBoundary();
            selectCurrentBasketId(basketId);
          }}
          handlePageChange={() => {}}
          handleBasketsRouting={() => router.push(PagesPath.BASKETS)}
          currentBasket={currentBasket}
          childrenEmpty={
            <Alert severity="info">
              <AlertTitle>
                <b>{t("basket.error.400.alert.title")}</b>
              </AlertTitle>
              {t("basket.error.400.alert.message")}
            </Alert>
          }
          KIcon={KIcon}
        />
        <BasketCreate />
        <BasketAddTo />
      </>
    );
  } else {
    return (
      <GenericErrorBoundary
        error={error}
        resetErrorBoundary={resetErrorBoundary}
        KIcon={KIcon}
        {...rest}
      />
    );
  }
};

export default BasketError;
